// The Activities dashboard for a job the map page ran on the data API (an area made on the catalogue 1.0 maps).
// The map page hands over the job's request; this runs it again in full, with its distributions
// (services/activitiesDashboard.ts), and draws the result with the demo build's dashboard. While the full exports'
// CSV route does not exist (telcofy-demo#110) it says so plainly; it never shows a preview as if it were the whole
// result.
import React, { useEffect, useState } from 'react';
import NavBar from '../../components/common/NavBar';
import DataStateNotice from '../../components/data/DataStateNotice';
import EeActivityDashboard from '../demo/EeActivityDashboard';
import type { EeDashboardState } from '../demo/EeActivityDashboard';
import { loadActivitiesDashboard } from '../../services/activitiesDashboard';
import type { CatalogueDashboardJob } from '../../services/activitiesDashboard';
import { FullResultsError } from '../../services/fullResults';
import { describeRequestError, isAbortError } from '../../services/http';
import type { EeJobResult } from '../../utils/eeActivities';

export interface CatalogueDashboardState extends Omit<EeDashboardState, 'ee'> {
  catalogueJob: CatalogueDashboardJob;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; ee: EeJobResult }
  | { kind: 'failed'; title: string; body: string };

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-paper-200">
    <NavBar title="Activity Dashboard" showBackButton={true} />
    <div className="max-w-6xl mx-auto p-6">{children}</div>
  </div>
);

const CatalogueActivityDashboard: React.FC<{ state: CatalogueDashboardState }> = ({ state }) => {
  const { params, day } = state.catalogueJob;
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    // Leaving the page stops waiting for the jobs (they keep running on the backend).
    const controller = new AbortController();
    setLoad({ kind: 'loading' });
    loadActivitiesDashboard(params, day, { signal: controller.signal })
      .then((ee) => setLoad({ kind: 'ready', ee }))
      .catch((e) => {
        if (isAbortError(e)) return;
        setLoad(
          e instanceof FullResultsError
            ? { kind: 'failed', title: "The complete results can't be read yet", body: `${e.message} The results on the map page are not affected.` }
            : { kind: 'failed', title: "The dashboard couldn't be built", body: describeRequestError(e) },
        );
      });
    return () => controller.abort();
  }, [params, day]);

  if (load.kind === 'ready') {
    return <EeActivityDashboard state={{ ee: load.ee, selectedGrids: state.selectedGrids, measure: state.measure, countryCode: state.countryCode }} />;
  }
  return (
    <Frame>
      {load.kind === 'loading' ? (
        <DataStateNotice
          tone="info"
          title="Reading the complete results"
          body="The dashboard runs this analysis again in full, with its distributions of arrivals. This can take a few minutes."
        />
      ) : (
        <DataStateNotice tone="error" title={load.title} body={load.body} />
      )}
    </Frame>
  );
};

export default CatalogueActivityDashboard;
